/**
 * Trello-specific types for MCP consent.
 *
 * These types are used by the TrelloWorkspaceSelector and McpContextProvider
 * to manage Trello workspace selection in the MCP consent flow.
 */

import { getConfig } from '../../../../config/envConfig';

/** A Trello workspace available for MCP consent selection. */
/**
 * Workspaces requested per page.
 *
 * Trello server caps this field at 128 (PAGE_SIZE_LIMIT in
 * memberWorkspacesArguments.ts). A smaller page keeps the first paint fast for
 * the common case of a handful of workspaces; further pages are fetched only
 * when the user scrolls to the bottom of the dropdown.
 */
export const TRELLO_WORKSPACE_PAGE_SIZE = 20;

export type TrelloWorkspace = {
  /** Trello workspace ARI (e.g. "ari:cloud:trello::workspace/6900ea2530b1740f229f09f0"). */
  id: string;
  /** Display name of the workspace (e.g. "My Team Board"). */
  displayName: string;
  /** Atlassian organization ID that owns the Trello workspace, when available. */
  atlassianOrganizationId?: string | null;
  /** URL slug of the workspace, used to build its trello.com address. */
  shortName?: string | null;
  /** Workspace logo URL, when the workspace has a custom logo set. */
  logoUrl?: string | null;
};

/**
 * Human-readable web address for a workspace, e.g. `trello.com/w/acmeteam`.
 *
 * The host is environment-specific (`trello.com` in production,
 * `trellis.coffee` in staging) and comes from envConfig.
 *
 * Trello slugs are alphanumeric, so a value containing a dot or slash is
 * already a full address and is returned unchanged. Returns `null` when the
 * workspace has no slug, so callers can fall back to the display name alone.
 */
export function getTrelloWorkspaceUrl(
  workspace: TrelloWorkspace,
): string | null {
  const { shortName } = workspace;
  if (!shortName) {
    return null;
  }

  return /[./]/.test(shortName)
    ? shortName
    : `${getConfig().trello.webHost}/w/${shortName}`;
}
